import * as yup from "yup";

import { fetchData } from "@utils/api";
import { APIMethods } from "@typeDefinitions";
import { ActionsDto, actionsSchema } from "@client/common";
import { MetadataSchema, Metadata } from "@typeDefinitions";
import qs from "qs";
import { TagResourceDto, tagResourceSchema } from "@client/searchRecipesNew";

export interface FetchPatientSharedRecipesParams {
  patientId: string;
  page?: number;
  perPage?: number;
  query?: string;
  status?: string;
  tags?: number[];
  nutrients?: string[];
}

export const fetchPatientSharedRecipes = async ({
  patientId,
  ...params
}: FetchPatientSharedRecipesParams): Promise<FetchPatientSharedRecipesResponse> => {
  const query = qs.stringify(params, {
    skipNulls: true,
    arrayFormat: "brackets",
  });

  const response = await fetchData<FetchPatientSharedRecipesResponse>(
    `/dietitian/patients/${patientId}/shared-content-recipes/search?${query}`,
    APIMethods.GET,
  );
  return fetchPatientSharedRecipesSchema.validate(response);
};

const macroSchema = yup.object().shape({
  id: yup.number().required(),
  value: yup.number().required(),
});

const mediaSchema = yup.object({
  url: yup.string().nullable().default(null),
});

const recipeSchema = yup.object().shape({
  id: yup.number().required(),
  title: yup.string().required(),
  titleEn: yup.string().nullable().default(null),
  servings: yup.number().required(),
  favorite: yup.array().of(yup.number().required()).required(),
  tags: yup.array().of(tagResourceSchema).required(),
  macros: yup.array().of(macroSchema).required(),
  actions: actionsSchema.required(),
  media: mediaSchema.required(),
});

const statusSchema = yup.object().shape({
  id: yup.number().required(),
});

const recipeItemSchema = yup.object().shape({
  recipe: recipeSchema.required(),
  status: statusSchema.required(),
  activeFrom: yup.string().required(),
});

const linksSchema = yup.object().shape({
  first: yup.string().required(),
  last: yup.string().required(),
  prev: yup.string().nullable().defined(),
  next: yup.string().nullable().defined(),
});

const fetchPatientSharedRecipesSchema = yup.object().shape({
  data: yup.array().of(recipeItemSchema).required(),
  links: linksSchema.required(),
  meta: MetadataSchema,
});

interface Macro {
  id: number;
  value: number;
}

interface MediaDto {
  url: string | null;
}

interface Recipe {
  id: number;
  title: string;
  titleEn: string | null;
  servings: number;
  favorite: number[];
  tags: TagResourceDto[];
  macros: Macro[];
  actions: ActionsDto;
  media: MediaDto;
}

interface Status {
  id: number;
}

interface RecipeItem {
  recipe: Recipe;
  status: Status;
  activeFrom: string;
}

interface Links {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface FetchPatientSharedRecipesResponse {
  data: RecipeItem[];
  links: Links;
  meta: Metadata;
}
